import {
  AppstoreAddOutlined,
  HighlightOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Input, List, Tooltip, Typography, Upload } from 'antd';
import axios from 'axios';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { ConversationContext } from '../../contexts/ConversationContext';
import { UserContext } from '../../contexts/UserContext';
import { apiUrl, colorPlaceholderAvatar, shuffle } from '../../utils/constants';
import { decrypt } from '../../utils/decrypt';
import AddGroupMemberForm from '../kits/AddGroupMemberForm';
import RemoveGroupMemberForm from '../kits/RemoveGroupMemberForm';

const { Search } = Input;
const { Paragraph } = Typography;

const ConversationTab = () => {
  const { conversationId } = useParams();
  const {
    authState: { user },
  } = useContext(AuthContext);
  const {
    getConversations,
    conversationState: { conversations },
    updateConversation,
  } = useContext(ConversationContext);
  const {
    userState: { users },
  } = useContext(UserContext);
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [array, setArray] = useState([]);

  useEffect(() => {
    const getApi = async () => {
      await Promise.all([getConversations()]);
      setArray(shuffle(colorPlaceholderAvatar));
    };
    getApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = useCallback((value) => {
    console.log(value);
  }, []);

  const changeGroupName = useCallback(
    (newName, item) => {
      if (newName === item?.name) return;
      updateConversation(conversationId, { name: newName });
    },
    [conversationId, updateConversation]
  );

  const handleCancelModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleCancelModalAdd = useCallback(() => {
    setOpenModalAdd(false);
  }, []);

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      action: `${apiUrl}/conversations/${conversationId}/avatar`,
      headers: {
        Authorization: axios?.defaults?.headers?.common['Authorization'],
        'x-yumyum-key': `${process.env.REACT_APP_X_YUMYUM_KEY}`,
      },
      method: 'put',
      showUploadList: false,

      beforeUpload: (file) => {
        const isPNG = file?.type === 'image/jpeg';
        if (!isPNG) {
          console.error(`${file?.name} is not a jpeg file`);
        }
        return isPNG || Upload?.LIST_IGNORE;
      },

      onChange(info) {
        if (info?.file?.status !== 'uploading') {
          console.log(info?.file, info?.fileList);
        }
        if (info?.file?.status === 'done') {
          console.log(`${info?.file?.name} file uploaded successfully`);
        } else if (info?.file?.status === 'error') {
          console.error(`${info?.file?.name} file upload failed.`);
        }
      },
    }),
    [conversationId]
  );

  return (
    <>
      <div
        style={{
          background: '#001529',
          display: 'flex',
          position: 'sticky',
          alignItems: 'center',
          padding: '16px 0px',
          zIndex: 2,
          top: 38,
        }}
      >
        <Search
          className="logo"
          placeholder="Search conversation"
          allowClear
          onSearch={onSearch}
        />
      </div>
      {user?.role !== 'USER' && (
        <Button
          block
          icon={<AppstoreAddOutlined />}
          ghost
          onClick={() => {
            navigate('/meeting');
          }}
        >
          Meeting room
        </Button>
      )}

      {conversations?.length > 0 && (
        <List
          dataSource={conversations}
          renderItem={(item, index) => (
            <List.Item
              key={item?.id || index + 1}
              className={`list-item ${
                item?.id === conversationId ? 'active' : ''
              }`}
              onClick={() => navigate(`/conversations/${item?.id}`)}
            >
              <List.Item.Meta
                style={{
                  margin: '0px 20px',
                  alignItems: 'center',
                }}
                avatar={
                  <Upload
                    {...uploadProps}
                    disabled={item?.type === 'CHAT_GROUP' ? false : true}
                  >
                    <Tooltip
                      title={item?.type === 'CHAT_GROUP' ? 'Change avatar' : ''}
                      placement="right"
                    >
                      <Avatar
                        style={{
                          backgroundColor:
                            array[
                              index -
                                array.length * Math.floor(index / array.length)
                            ],
                        }}
                        size="large"
                        src={item?.avatar?.imageUrl || item?.avatar}
                      >
                        {(!item?.avatar?.imageUrl && item?.type === 'CHAT_GROUP'
                          ? item?.name
                          : item?.users?.find(
                              (subUser) => subUser?.id !== user?.id
                            )?.fullName
                        )
                          ?.match(/\b(\w)/g)
                          ?.join('')
                          ?.substring(0, 2)
                          ?.toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  </Upload>
                }
                title={
                  item?.type !== 'CHAT_GROUP' ? (
                    <strong style={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                      {
                        item?.users?.find((subUser) => subUser?.id !== user?.id)
                          ?.fullName
                      }
                    </strong>
                  ) : (
                    <Paragraph
                      editable={{
                        icon: <HighlightOutlined style={{ color: 'orange' }} />,
                        tooltip: 'click to edit text',
                        onChange: (string) => changeGroupName(string, item),
                        maxLength: 40,
                      }}
                      style={{
                        color: 'rgba(255, 255, 255, 0.65)',
                        fontWeight: 'bold',
                      }}
                    >
                      {item?.name}
                    </Paragraph>
                  )
                }
                description={
                  <small style={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                    {item?.messages?.length > 0
                      ? decrypt(item?.messages[0]?.content)
                      : ''}
                  </small>
                }
              />
              {item?.type === 'CHAT_GROUP' && (
                <Button
                  type="primary"
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  style={{
                    marginRight: '10px',
                  }}
                  size="small"
                >
                  <UsergroupAddOutlined
                    size="small"
                    onClick={() => setSelectedItem(item)}
                  />
                </Button>
              )}
            </List.Item>
          )}
        />
      )}
      <RemoveGroupMemberForm
        openModal={openModal}
        handleCancelModal={handleCancelModal}
        conversation={selectedItem}
        setOpenModalAdd={setOpenModalAdd}
      />
      <AddGroupMemberForm
        openModalAdd={openModalAdd}
        handleCancelModalAdd={handleCancelModalAdd}
        conversation={selectedItem}
        users={users}
        handleCancelModal={handleCancelModal}
      />
    </>
  );
};

export default memo(ConversationTab);
